/**
 * Suspensions — Iteration 21C-C Phase 2 wired page.
 *
 * Lists SUSPENDED and REVOKED institutions with lifecycle metadata and
 * lets an operator trigger the CAP deadline cron sweep.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Suspensions() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/institutions");
      const items = (r.data?.items || []).filter(
        (i) => i.status === "SUSPENDED" || i.status === "REVOKED"
      );
      setRows(items);
    } catch (e) {
      toast.error("Failed to load institutions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runCron = async () => {
    setBusy(true);
    try {
      const r = await api.post("/cap/cron/check-deadlines", {});
      toast.success("Deadline sweep executed", {
        description: `Processed ${r.data?.processed ?? 0} institution(s).`,
      });
      await load();
    } catch (e) {
      toast.error("Sweep failed", { description: e?.response?.data?.detail?.code || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-page-suspensions">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Suspensions</h1>
          <p className="text-sm text-muted-foreground">
            Institutions currently frozen (SUSPENDED) or terminally revoked (REVOKED). Reinstatement is
            performed from the CAP Reviews queue once a plan is approved.
          </p>
        </div>
        <Button size="sm" onClick={runCron} disabled={busy} data-testid="suspensions-run-cron">
          Run deadline sweep
        </Button>
      </div>

      <div className="border rounded overflow-hidden">
        <table className="w-full text-sm" data-testid="suspensions-table">
          <thead className="bg-muted/30 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Institution</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Suspended</th>
              <th className="text-left p-3">CAP deadline</th>
              <th className="text-left p-3">Reinstatement earliest</th>
              <th className="text-left p-3">Revoked</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">
                No suspended or revoked institutions.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.vlk_inst_id} className="border-t" data-testid={`suspensions-row-${r.vlk_inst_id}`}>
                <td className="p-3">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">{r.vlk_inst_id}</div>
                </td>
                <td className="p-3">
                  <span className={`text-xs uppercase tracking-widest px-2 py-0.5 rounded ${
                    r.status === "REVOKED" ? "bg-destructive/10 text-destructive"
                                            : "bg-yellow-500/15 text-yellow-700 dark:text-yellow-500"
                  }`}>
                    {r.status}
                  </span>
                </td>
                <td className="p-3 font-mono text-xs">{fmt(r.suspended_at)}</td>
                <td className="p-3 font-mono text-xs">{fmt(r.cap_deadline_at)}</td>
                <td className="p-3 font-mono text-xs">{fmt(r.suspension_earliest_reinstatement)}</td>
                <td className="p-3 font-mono text-xs">{fmt(r.revoked_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  return String(iso).slice(0, 19).replace("T", " ");
}
